<template>
  <div class="instapro-widget">
    <!-- Widget Components -->
    <TheOpenTab v-show="collapsed" />
    <TheCoachListDrawer v-show="!collapsed" @click.native="toggleModalFocus(false)" />

    <!-- Modals -->
    <TheWidgetModals />

    <ProAnalysis
      v-if="isGameplayAnalysisModalOpen"
      v-scroll-lock="
        isGameplayAnalysisModalOpen &&
        [GAMEPLAY_UPLOAD_STEPS.HOW_TO, GAMEPLAY_UPLOAD_STEPS.REQUEST_RECEIVED].includes(gameplayUploadStep)
      "
    />
    <div class="modal-container" v-if="inSubscription">
      <ActiveSubscription
        v-if="subscribedToCoach"
        @closeModal="setPaymentModalShow(false)"
      />
      <PurchaseModal
        v-else
        @closePaymentModal="setPaymentModalShow"
        :subscription="true"
        :productType="PRODUCT_TYPE.COACH_SUBSCRIPTION"
      />
    </div>
    <ScheduleModal
      v-if="showScheduleModal"
      @close="setShowScheduleModal(false)"
    />
    <ReviewContainer v-if="step === COACHING_STEP.REVIEW" />
    <div
      class="purchase-modal-container"
      v-if="products.length && paymentModalShow"
    >
      <PaymentModal
        v-scroll-lock="true"
        @closePaymentModal="paymentModalShow = false"
        :products="products"
        :initTab="paymentMethod"
        :redirectPath="redirectPath"
        @extraAction="paymentModalShow = false"
      />
    </div>
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from "vuex"
import moment from "moment"
import { ROUTER_NAMES } from "@/constants/router.constants"
import { COACHING_STEP, SESSION_REQUEST_STATUSES } from "@/constants/coaching"
import { WIDGET_STATES } from "@/store/coaching/widget/widget.constants"
import { PRODUCT_TYPE } from "@/constants/payment"
import { GAME_KEYS } from "@/store/game/game.constants"
import { COACH_SERVICE_CHECKOUT_FLOWS } from "@/store/coaching/coaching.constants"
import { GAMEPLAY_UPLOAD_STEPS } from "@/store/coaching/gameplay-analysis/gameplay-analysis.constants"
import { getCoachingWidgetStorageCollapsed } from "@/utils/coachingWidget.storage"

export default {
  components: {
    TheOpenTab: () => import("./TheOpenTab"),
    TheCoachListDrawer: () => import("./TheCoachListDrawer"),
    TheWidgetModals: () => import("./TheWidgetModals"),
    ProAnalysis: () => import("./pro_analysis/ProAnalysis"),
    PurchaseModal: () => import("@/components/payment_v2/PaymentModal.vue"),
    ActiveSubscription: () =>
      import("@/components/instapro_widget/ActiveSubscription.vue"),
    ScheduleModal: () =>
      import("@/components/instapro_widget/ScheduleModal.vue"),
    ReviewContainer: () =>
      import("@/components/coach_profile/ReviewContainer.vue"),
    PaymentModal: () => import("@/components/payment/PaymentModal.vue"),
  },
  data: function () {
    return {
      PRODUCT_TYPE: PRODUCT_TYPE,
      inVodProcess: this.$route.query["vod-process"] || false,
      inSubscription: false,
      inCoachService: this.$route.query["coach-service"] || false,
      subscribedToCoach: false,
      inScheduleProcess: this.$route.query["schedule-process"] || false,
      personalChannel: null,
      newCoachChannel: null,
      coachChannels: {},
      COACHING_STEP,
      coachSubscribed: this.$route.query["coach_subscription"] || false,
      paymentModalShow: false,
      products: [],
      paymentMethod: "",
    }
  },
  computed: {
    ...mapGetters("Util", [
      "isMobile",
    ]),
    ...mapGetters("Pusher", [
      "coachPusherClient",
    ]),
    ...mapGetters("User/Coaching/", [
      "getInstaProUser",
    ]),
    ...mapGetters("Coaching", [
      "coachingGame",
      "activeSessionRequest",
    ]),
    ...mapGetters("Coaching/CoachWidget", [
      "collapsed",
      "selectedGame",
      "widgetState",
      "coachByUserId",
      "redirectCoachUsername",
      "currentRequest",
    ]),
    ...mapGetters("Coaching/CoachService", [
      "step",
    ]),
    ...mapGetters("Coaching/GameplayAnalysis", {
      isGameplayAnalysisModalOpen: "isModalVisible",
    }),
    ...mapGetters("Coaching/GameplayAnalysis", [
      "gameplayUploadStep",
    ]),
    ...mapGetters("Coaching/Schedule", [
      "showScheduleModal",
    ]),
    redirectPath () {
      return window.location.href
    },
  },
  methods: {
    ...mapActions("Pusher", [
      "initCoachPusher",
    ]),
    ...mapMutations("Store", [
      "setSelectedProduct",
    ]),
    ...mapMutations("Payment", [
      "setPaymentIntent",
    ]),
    ...mapActions("Game", [
      "fetchInstaproGames",
    ]),
    ...mapActions("Instapro/Vod", [
      "setCheckoutRestoreState",
    ]),
    ...mapActions("User", [
      "loadUser",
      "getUserSubscriptions",
    ]),
    ...mapActions("User/Inventory", [
      "loadInventory",
    ]),
    ...mapActions("User/Coaching", [
      "loadInstaProUser",
    ]),
    ...mapActions("Message", [
      "checkConversationInSession",
    ]),
    ...mapActions("Coaching", [
      "loadInstaProGames",
      "loadCoachingGame",
      "loadActiveSessionRequest",
      "getActiveSessionRequestById",
      "loadFavoriteCoaches",
    ]),
    ...mapMutations("Coaching/CoachService", [
      "setStep",
      "setSelectedService",
      "setServiceType",
      "setServiceQuantity",
    ]),
    ...mapMutations("Coaching/Schedule", [
      "setCoach",
      "setGameKey",
      "setType",
      "setQuantity",
      "setScheduledSlots",
      "setCurrentSession",
      "setShowScheduleModal",
    ]),
    ...mapActions("Coaching/CoachWidget", [
      "toggleDrawerCollapse",
      "setDrawerHighlighted",
      "setWidgetState",
      "setSelectedGame",
      "toggleModalFocus",
      "loadCoach",
      "loadAllFeaturedCoaches",
      "loadAllAvailableCoaches",
      "loadFirstPageOfOfflineCoaches",
      "updateCoaches",
      "setSelectedCoach",
      "setRedirectCoachId",
      "setRedirectCoachUsername",
      "checkRedirectCoach",
      "setUserInSession",
      "setCurrentRequestId",
      "setCurrentRequestCoach",
      "setCurrentRequestType",
      "setCurrentRequestGames",
      "setCurrentRequestGame",
      "setCurrentRequestCommunicationMethod",
      "setCurrentRequestCreated",
    ]),
    ...mapActions("Coaching/CoachWidget/ServiceCheckout", [
      "setCoachUnavailable",
      "setCoachRejected",
    ]),
    ...mapActions("Coaching/GameplayAnalysis", [
      "startGameplayAnalysisFlow",
    ]),
    ...mapActions("Game", [
      "fetchPgGames",
    ]),
    ...mapActions("Game/LeagueOfLegends", [
      "loadChampions",
    ]),
    ...mapActions("Game/SSBU", [
      "loadCharacters",
    ]),
    listenStartSubscribe () {
      document.addEventListener("startCoachSubscribe", e => {
        const subscription = e.detail.subscription
        const subscribedToCoach = e.detail.subscribedToCoach
        this.subscribedToCoach = subscribedToCoach
        this.setSelectedProduct(subscription.product)
        this.setPaymentModalShow(true)
      })
    },
    listenScheduleEvent () {
      document.addEventListener("scheduleTime", e => {
        this.setCoach(e.detail.coach)
        this.setGameKey(e.detail.game)
        this.setType(e.detail.type)
        this.setQuantity(e.detail.qty)
        this.setShowScheduleModal(true)
      })
    },
    listenSelectProduct () {
      document.addEventListener("selectProduct", e => {
        this.setPaymentIntent(null)
        this.products = [e.detail.product]
        this.paymentMethod = e.detail.paymentMethod
        this.paymentModalShow = true
      })
    },
    listenOpenCoachProfile () {
      document.addEventListener("openCoachProfileEvent", e => {
        const game = e.detail?.game
        if (game) {
          this.setSelectedGame = game
        }
        const coachUserId = e.detail?.coachId
        const coach = this.coachByUserId(coachUserId)
        if (coach) {
          this.setSelectedCoach(coach)
          this.setWidgetState(WIDGET_STATES.SELECTING)
        } else {
          const coachUsername = e.detail?.coachUsername
          this.setRedirectCoachUsername(coachUsername)
          this.checkRedirectCoach()
        }
      })
    },
    listenOpenCoachDrawer () {
      document.addEventListener("openInstacoachDrawer", e => {
        if (!this.collapsed) {
          this.setDrawerHighlighted(true)
          setTimeout(() => {
            this.setDrawerHighlighted(false)
          }, 200)
        }
        this.toggleDrawerCollapse(false)
      })
      document.addEventListener("openCoachDrawerEvent", e => {
        this.toggleDrawerCollapse(false)
      })
    },
    listenGameSelect () {
      document.addEventListener("selectCoachListGameSelectGame", e => {
        const game = e.detail.game
        this.setSelectedGame(game)
      })
    },
    listenInventoryUpdate () {
      document.addEventListener("userInventoryUpdated", e => {
        this.loadInventory()
      })
    },
    listenStripePaymentSuccess () {
      document.addEventListener("stripePaymentSucceeded", e => {
        this.setWidgetState(WIDGET_STATES.CREDIT_CARD_SUCCESS)
      })
    },
    async continueVodProcess () {
      await this.setCheckoutRestoreState(true)
      this.startGameplayAnalysisFlow("Continuing from Checkout redirect")
    },
    confirmCoachSubscribed () {
      const subscribedItem = JSON.parse(
        window.sessionStorage.getItem("subscribedProduct")
      )
      const subscribedCoach = JSON.parse(
        window.sessionStorage.getItem("subscribedCoach")
      )
      this.setStep(COACHING_STEP.SUBSCRIBED)
      const game = subscribedItem.product.perks[0].game
      const coachUsername = subscribedCoach.coach.coachUsername
      this.$router.push(`/coach/${coachUsername}/${game}`)
    },
    async continueScheduling () {
      const coach = JSON.parse(window.localStorage.getItem("scheduleCoach"))
      const quantity = JSON.parse(
        window.localStorage.getItem("scheduleQuantity")
      )
      const type = JSON.parse(window.localStorage.getItem("scheduleType"))
      const gameKey = JSON.parse(window.localStorage.getItem("scheduleGameKey"))
      const scheduledSlots = JSON.parse(
        window.localStorage.getItem("scheduledSlots")
      )
      const currentSession = JSON.parse(
        window.localStorage.getItem("scheduleCurrentSession")
      )
      await Promise.all([
        this.setCoach(coach),
        this.setQuantity(quantity),
        this.setType(type),
        this.setGameKey(gameKey),
        this.setScheduledSlots(scheduledSlots),
        this.setCurrentSession(currentSession),
      ])
      this.setShowScheduleModal(true)
    },
    setPaymentModalShow (show) {
      this.inSubscription = show
    },
    continueCoachService () {
      const serviceCheckoutFlow = window.sessionStorage.getItem("serviceCheckoutFlow")
      switch (serviceCheckoutFlow) {
        // Restore checkout process through coach's profile
        case COACH_SERVICE_CHECKOUT_FLOWS.COACH_PROFILE:
          const scheduledSlotsJSON = window.sessionStorage.getItem("scheduledSlots")
          let scheduledSlots = []
          if (scheduledSlotsJSON) {
            scheduledSlots = JSON.parse(scheduledSlotsJSON)
          }
          this.setScheduledSlots(scheduledSlots)

          const currentSession = window.sessionStorage.getItem("scheduleCurrentSession")
          this.setCurrentSession(JSON.parse(currentSession))

          const selectedService = window.sessionStorage.getItem("selectedService")
          this.setSelectedService(JSON.parse(selectedService))

          const serviceType = window.sessionStorage.getItem("serviceType")
          this.setServiceType(JSON.parse(serviceType))

          const serviceQuantity = window.sessionStorage.getItem("serviceQuantity")
          this.setServiceQuantity(JSON.parse(serviceQuantity))

          this.setStep(COACHING_STEP.SELECTING)

          const coachUsername = JSON.parse(
            window.sessionStorage.getItem("serviceCoachUsername")
          )
          const game = JSON.parse(window.sessionStorage.getItem("serviceGame"))
          this.$router.push(`/coach/${coachUsername}/${game}?no-reset=1`)
          break
        // Restore state for the widget
        case COACH_SERVICE_CHECKOUT_FLOWS.COACH_WIDGET:
          const coachUserId = window.sessionStorage.getItem("selectedCoachUserId")
          this.setRedirectCoachId(coachUserId)
          this.checkRedirectCoach()
          break
      }
    },
    async checkActiveSessionRequest () {
      await this.loadActiveSessionRequest()
      if (this.activeSessionRequest) {
        const status = this.activeSessionRequest.status
        const coachUserId = this.activeSessionRequest.coach_id
        await this.loadCoach(coachUserId)
        const coach = this.coachByUserId(coachUserId)
        switch (status) {
          case SESSION_REQUEST_STATUSES.ACCEPTED:
            this.setSelectedCoach(coach)
            break
          case SESSION_REQUEST_STATUSES.OPEN:
            this.setSelectedCoach(coach)
            break
        }

        this.setCurrentRequestId(this.activeSessionRequest.id)
        this.setCurrentRequestCoach(coach)
        this.setCurrentRequestType(this.activeSessionRequest.type)
        this.setCurrentRequestGames(parseInt(this.activeSessionRequest.value))
        this.setCurrentRequestGame(this.activeSessionRequest.game?.name)
        this.setCurrentRequestCommunicationMethod(_.first(Object.keys(this.activeSessionRequest.communication || {})))
        this.setCurrentRequestCreated(moment.utc(this.activeSessionRequest.updated_at))

        this.handleRequestStatus(status)
      }
    },
    async subscribePersonalChannel () {
      if (!this.personalChannel && this.getInstaProUser) {
        const channel = this.getInstaProUser.broadcast_channel
        this.personalChannel = this.coachPusherClient.subscribe(channel)
        this.personalChannel.bind(
          "request-update-id-only",
          this.handleRequestUpdate
        )
      }
    },
    async handleRequestUpdate (data) {
      const requestId = data.request_id
      await this.getActiveSessionRequestById(requestId)
      this.checkActiveSessionRequest()
    },
    handleRequestStatus (status) {
      const routeName = this.$route.name
      const onCoachProfile = routeName === ROUTER_NAMES.COACH_PROFILE
      if (status === SESSION_REQUEST_STATUSES.OPEN) {
        this.setStep(COACHING_STEP.PENDING)
        if (!onCoachProfile) {
          this.setWidgetState(WIDGET_STATES.WAITING)
        }
      }
      if (status === SESSION_REQUEST_STATUSES.REJECTED) {
        this.setStep(COACHING_STEP.FAIL)
        if (!onCoachProfile) {
          this.setCoachRejected(true)
          this.setCoachUnavailable(true)
        }
      }
      if (status === SESSION_REQUEST_STATUSES.CLOSED) {
        this.setStep(COACHING_STEP.FAIL)
        if (!onCoachProfile) {
          this.setCoachUnavailable(true)
        }
      }
      if (
        [
          SESSION_REQUEST_STATUSES.REJECTED,
          SESSION_REQUEST_STATUSES.CLOSED,
        ].includes(status)
      ) {
        this.setStep(COACHING_STEP.FAIL)
      }
      if (status === SESSION_REQUEST_STATUSES.ACCEPTED) {
        this.trackAnalyticsEvent("Enter Session", {
          quantity: this.currentRequest?.games || 1,
          session_type: this.currentRequest?.type,
        })
      }
      if (
        [
          SESSION_REQUEST_STATUSES.ACCEPTED,
          SESSION_REQUEST_STATUSES.IN_SESSION,
        ].includes(status)
      ) {
        this.setStep(COACHING_STEP.IN_SESSION)
        this.checkConversationInSession()
        this.loadInventory(true)
        this.setUserInSession(true)
        if (!onCoachProfile) {
          this.setWidgetState(null)
        }
      }
      if (
        [
          SESSION_REQUEST_STATUSES.FINISHED,
          SESSION_REQUEST_STATUSES.CLOSED,
        ].includes(status)
      ) {
        if (this.step === COACHING_STEP.IN_SESSION) {
          this.checkConversationInSession()
          this.setStep(COACHING_STEP.REVIEW)
          this.setUserInSession(false)
        }
      }
    },
    subscribeNewCoachChannel () {
      if (!this.newCoachChannel && this.getInstaProUser) {
        const channel = "coach-new-v2"
        this.newCoachChannel = this.coachPusherClient.subscribe(channel)
        this.newCoachChannel.bind(
          "coach-update-v2",
          this.pusherCoachUpdateHandlerNew
        )
      }
    },
    async pusherCoachUpdateHandlerNew (data) {
      const coach = data.coach
      if (!coach) {
        return
      }

      const coachId = coach.id
      const coachUserId = coach.user_id
      const pusherChannel = coach.broadcast_channel
      this.bindCoachUpdateHandler(coachId, pusherChannel)

      await this.loadCoach(coachUserId)
    },
    bindCoachUpdateHandler (coachId, pusherChannel) {
      if (!this.coachChannels[coachId]) {
        this.coachChannels[coachId] = this.coachPusherClient.subscribe(pusherChannel)
        this.coachChannels[coachId].bind("coach-update-v2", this.pusherCoachUpdateHandler)
      }
    },
    pusherCoachUpdateHandler (data) {
      // TODO: Resolve inconsistent format of updated data here...
      this.updateCoaches([data])
    },
  },
  async created () {
    this.GAMEPLAY_UPLOAD_STEPS = GAMEPLAY_UPLOAD_STEPS

    const isCollapsedInStorage = getCoachingWidgetStorageCollapsed()
    if (isCollapsedInStorage != null) {
      this.toggleDrawerCollapse(isCollapsedInStorage)
    }

    await Promise.all([
      this.loadUser(),
      this.fetchPgGames(),
      this.loadInstaProGames(), // TODO: Resolve redundant Game data stores
      this.fetchInstaproGames(),
    ])

    await Promise.all([
      this.getUserSubscriptions(),
      this.loadInstaProUser(),
      this.loadInventory(),
      this.loadCoachingGame(),
    ])

    // Sync initial widget game selection to general coach state
    this.setSelectedGame(this.coachingGame)

    this.loadFavoriteCoaches()
    this.loadAllFeaturedCoaches()
    this.loadAllAvailableCoaches()
    this.loadFirstPageOfOfflineCoaches()

    // Set up DOM event listeners
    this.listenStartSubscribe()
    this.listenScheduleEvent()
    this.listenSelectProduct()
    this.listenOpenCoachProfile()
    this.listenOpenCoachDrawer()
    this.listenGameSelect()
    this.listenInventoryUpdate()
    this.listenStripePaymentSuccess()

    // Set up Pusher channel listeners
    await this.initCoachPusher()
    this.subscribePersonalChannel()
    this.subscribeNewCoachChannel()

    // Restore state (if a user refreshed the page, for instance)
    if (this.inCoachService) {
      this.continueCoachService()
    }
    if (this.inVodProcess) {
      this.continueVodProcess()
    }
    if (this.inScheduleProcess) {
      this.continueScheduling()
    }
    if (this.coachSubscribed) {
      this.confirmCoachSubscribed()
    }
    this.checkActiveSessionRequest()
  },
  watch: {
    collapsed: function (newCollapsed, oldCollapsed) {
      if (!newCollapsed) {
        this.trackAnalyticsEvent("Open Instapro", {
          game: this.selectedGame,
        })
      }
    },
    selectedGame: function (game) {
      switch (game) {
        case GAME_KEYS.LEAGUE_OF_LEGENDS:
          this.loadChampions()
          break
        case GAME_KEYS.SSBU:
          this.loadCharacters()
          break
      }
    },
    widgetState: function (state) {
      if (state !== null) {
        this.$nextTick(() => {
          this.toggleModalFocus(true)
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/_mixins.scss";
@import "@/assets/style/_variables.scss";

.instapro-widget {
  text-align: left;
  left: $coach-list-offset;
  z-index: 10;

  @include tablet-down {
    left: 0;
  }

  &.drawer-open {
    @include phone-down {
      z-index: 13;
    }
  }

  &--hidden {
    display: none;
  }

  >>> .avatar__badge {
    &--online {
      background-color: #28dd88;
    }

    &--busy {
      background-color: #ff8b26;
    }

    &--offline {
      background-color: #fff;
      border-color: #28dd88 !important;
      &.in-widget {
        background-color: #1f2024;
      }
    }
  }
}

.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 25;
  font-family: Proxima Nova;
  color: #3b3b3b;
  display: flex;
  align-items: center;
  justify-content: center;
}
.purchase-modal-container {
  position: fixed;
  z-index: 15;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/instapro_widget/InstaproWidget.vue